import { graphql } from '@/gql';
import type {
  DeliveryAddressInputType,
  EstimateAdditionalChargesInput,
  GetPreEstimateQuery,
  GourmetChargeOptionsInput,
  PromotionInput,
  StoreItemDetailsInput,
} from '@/gql/graphql';
import { queryExecute } from '@/Utils/graphqlAPI';

export type GourmetGetPreEstimateInput = {
  planName: string;
  items: StoreItemDetailsInput;
  address: DeliveryAddressInputType;
  additionalCharges: EstimateAdditionalChargesInput;
  chargeOptions?: GourmetChargeOptionsInput;
  promotion?: PromotionInput;
};

export type GourmetGetPreEstimateOutput = GetPreEstimateQuery['getPreEstimate'];

export const gourmetGetPreEstimate = async ({
  planName,
  items,
  address,
  additionalCharges,
  chargeOptions,
  promotion,
}: GourmetGetPreEstimateInput) => {
  const response = await queryExecute({
    query: graphql(`
      query getPreEstimate(
        $planName: String!
        $items: [StoreItemDetailsInput!]!
        $address: DeliveryAddressInputType!
        $additionalCharges: EstimateAdditionalChargesInput!
        $chargeOptions: GourmetChargeOptionsInput
        $promotion: PromotionInput
      ) {
        getPreEstimate(
          planName: $planName
          items: $items
          address: $address
          additionalCharges: $additionalCharges
          chargeOptions: $chargeOptions
          promotion: $promotion
        ) {
          noAsapDelivery
          total
          storeId
          menuId
          charges {
            title
            value
            originalValue
            tooltips {
              title
              value
            }
          }
          service
          timeEstimate {
            min
            max
          }
          userCharges {
            credits
            card
            expiringCredits
          }
          accountId
          currency
          serviceEstimateReference
          orderOptions {
            storeInstructions
            deliveryInstructions
            dropoffOptionId
          }
          address {
            placeId
            latitude
            longitude
            name
            formattedAddress
            streetNumber
            postalCode
            streetAddress
            streetAddress2
            state
            city
            country
          }
          additionalCharges {
            tip
            serviceFee
          }
          chargeOptions {
            plan
            tier
            applyDiscountedFee
            noServiceFee
            doNotCharge
            chargeNow
            reason
          }
          callSid
          promotion {
            promotionUuid
            text
            description
          }
          isSpecialInstructionsDisabled
          items {
            item {
              id
              storeId
              storeTitle
              title
              subtitle
              service
              price
              quantity
              currency
              specialInstructions
              serviceItemReference
              substitutionPreference
            }
            options {
              ...OptionsFields
              values {
                ...ValuesFields
                subOptions {
                  ...OptionsFields
                  values {
                    ...ValuesFields
                    subOptions {
                      ...OptionsFields
                      values {
                        ...ValuesFields
                        subOptions {
                          ...OptionsFields
                          values {
                            ...ValuesFields
                            subOptions {
                              ...OptionsFields
                              values {
                                ...ValuesFields
                                subOptions {
                                  ...OptionsFields
                                  values {
                                    ...ValuesFields
                                    subOptions {
                                      ...OptionsFields
                                      values {
                                        ...ValuesFields
                                        subOptions {
                                          ...OptionsFields
                                          values {
                                            ...ValuesFields
                                            subOptions {
                                              ...OptionsFields
                                              values {
                                                ...ValuesFields
                                                subOptions {
                                                  ...OptionsFields
                                                  values {
                                                    ...ValuesFields
                                                  }
                                                }
                                              }
                                            }
                                          }
                                        }
                                      }
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      fragment OptionsFields on ItemOptionsType {
        id
        title
        subtitle
        max
        min
        type
        minAggregateOptionsQuantity
        maxAggregateOptionsQuantity
        selectedValues
      }
      fragment ValuesFields on ItemOptionsValuesType {
        id
        title
        subtitle
        price
        max
        min
        default
        nextCursor
      }
    `),
    variables: {
      planName,
      items,
      address,
      additionalCharges,
      chargeOptions,
      promotion,
    },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  return response;
};
